/* eslint-disable react/prop-types */
/* eslint-disable react-native/no-inline-styles */
/*
Author: Chaitanya
*/

import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { View, StyleSheet, TouchableOpacity, Dimensions } from 'react-native';
import Toast from 'react-native-toast-message';
import { FontAwesome } from '@expo/vector-icons';

import SociollaText from '@components/sociolla/SociollaText';
import ItemDetail from '@components/global/ItemDetail';
import Button from '@components/form/Button';
import * as Layout from '@components/screen/Layout';
import RenderIf from '@components/global/RenderIf';
import StatusBadge from '@components/global/StatusBadge';
import ReminderNotice from '@components/global/ReminderNotice';
import FileInput from '@components/form/FileInput';
import AttachmentsInput from '@components/form/AttachmentsInput';
import Image from '@components/global/Image';
import SkpInternalBrandBudgetTable from './components/SkpExternalBudgetTable';
import ModalReject from '@components/customer/ModalReject';
import ModalCancel from '@components/modal/ModalCancel';
import ModalLog from '@components/modal/ModalLog';
import SelectBox from '@components/form/SelectBox';
import ScrollContainer from '@components/global/ScrollContainer';
import FloatCommentButton from '@components/comment/FloatCommentButton';
import CommentDrawer from '@components/comment/CommentDrawer';

import Colors from '@global/colors';
import { isWeb, isWebPlatform, isAndroid, isNative } from '@global/screens';
import {
  hasNoAccess,
  parseDate,
  currency,
  downloadFile,
  formatCoaTaxLabel,
  exportPdf,
  isSkpType,
  getFileNameFromUrl,
  getSkpJoinedReminderDays,
} from '@global/helpers';
import useApi from '@/hooks/useApi';
import { MsOrder } from '@api/ms-order';
import { translate } from '@global/translate';
import { showAlert, setTempData } from '@/store/global';
import { showPopup } from '@/store/modal';
import { SKP_EXTERNAL_LOG_FORMATTER } from '@/constants/log';

const img = require('@assets/img/default/user-image.png');
const fullWidth = Dimensions.get('screen').width;

const SkpExternalDetailScreen = (props) => {
  const dispatch = useDispatch();
  const userRole = useSelector((state) => state.auth?.user?.role?.code);
  const userData = useSelector((state) => state.auth?.user);
  // re-render on language change so the translated joined reminder copy follows it
  useSelector((state) => state.language.selected);

  const [detailData, setDetailData] = useState({});
  const [showComments, setShowComments] = useState(
    props.route.params?.openComments === 'true',
  );
  const [skpInternalsBrandBugets, setSkpInternalsBrandBugets] = useState([]);
  const [skpInternalsTableLoading, setSkpInternalsTableLoading] =
    useState(false);
  const [modalReject, setModalReject] = useState(false);
  const [modalCancel, setModalCancel] = useState(false);
  const [modalDelete, setModalDelete] = useState(false);
  const [showModalLog, setShowModalLog] = useState(false);
  const [taxesData, setTaxesData] = useState([]);
  const [vatsData, setVatsData] = useState([]);
  const [skpExternalCount, setSkpExternalCount] = useState(0);
  const [isInvalidTax, setIsInvalidTax] = useState(false);
  const [afterAction, setAfterAction] = useState(false);

  const getSkpExternalDetail = useApi(MsOrder.getSkpExternalDetail);
  const getAllSkpExternalsBrandBudget = useApi(
    MsOrder.getAllSkpExternalsBrandBudget,
  );
  const getCoaTaxSettingsDetail = useApi(MsOrder.getCoaTaxSettingsDetail);
  const getSkpExternalCount = useApi(MsOrder.getSkpExternalCount);

  const MobileScroll = !isWeb ? ScrollContainer : View;

  const showEditButton = !hasNoAccess([
    'superadmin',
    'regional_manager',
    'regional_support',
    'key_account_manager',
    'group_key_account_manager',
    'national_key_account_manager',
    'sales_admin',
    'salesman',
    'marketing_admin',
    'general_manager_marketing',
    'general',
  ]);
  const showCancelButton = !hasNoAccess([
    'superadmin',
    'general',
    'regional_manager',
    'general_manager_marketing',
    'marketing_admin',
    'group_key_account_manager',
    'national_key_account_manager',
  ]);
  const showDeleteButton = !hasNoAccess([
    'superadmin',
    'general',
    'regional_manager',
    'general_manager_marketing',
    'marketing_admin',
    'group_key_account_manager',
    'national_key_account_manager',
  ]);
  const showCompletedButton = !hasNoAccess([
    'superadmin',
    'marketing_admin',
    'general',
  ]);
  const canApprove = () => {
    if (detailData?.status == 'need_approval' && detailData?.current_approval) {
      const hasSalesTeam = [
        'branch_head',
        'sales_supervisor',
        'sales_admin',
        'finance_supervisor',
        'regional_manager',
        'regional_support',
        'key_account_manager',
        'group_key_account_manager',
        'national_key_account_manager',
        'salesman',
        'regional_distributor_manager',
      ].includes(userRole);
      const userSalesTeams = (userData?.sales_team || []).map((st) =>
        st.id?.toString(),
      );

      const skipSalesTeamCheck = [
        'national_key_account_manager',
        'tax_support',
      ].includes(userRole);

      return Boolean(
        detailData?.current_approval == userRole &&
          (skipSalesTeamCheck ||
            !hasSalesTeam ||
            (hasSalesTeam &&
              userSalesTeams.includes(detailData?.sales_team?.id?.toString()))),
      );
    } else {
      return false;
    }
  };

  const canEditTax = useMemo(() => {
    if (
      detailData?.status == 'need_approval' &&
      ['finance_head', 'tax_support', 'superadmin', 'general'].includes(
        userRole,
      )
    ) {
      return true;
    }

    return false;
  }, [userRole, detailData]);

  const isGt = useMemo(
    () =>
      detailData?.skp_internal
        ? isSkpType(detailData.skp_internal, 'gt')
        : false,
    [detailData],
  );

  const User = (userProps) => {
    return (
      <View
        style={{
          flex: 1,
          flexDirection: 'row',
          justifyContent: 'flex-start',
          ...userProps.style,
        }}
        key={'list-users-' + userProps.index}>
        <Image
          style={{ width: 20, height: 20, marginRight: 8 }}
          width={20}
          height={20}
          isResolveAsset
          source={userProps.image ? userProps.image : img}
        />
        <SociollaText
          style={{ fontSize: 12, color: Colors.blue3, alignSelf: 'center' }}>
          {userProps.name}
        </SociollaText>
      </View>
    );
  };

  const getDetail = async (id) => {
    const response = await getSkpExternalDetail.request(id);
    if (response?.data?.data && Object.keys(response.data.data).length) {
      const result = response.data.data;
      result.id = result._id;
      if (result.customer?.id) {
        getSkpInternalsBrandBudgets(
          result.id,
          result.customer.id,
          result.outlet?.id,
        );
      }
      if (result?.documents?.length) {
        result.documents.map((doc) => {
          if (doc.type == 'kwitansi') {
            result.kwitansi = doc.url;
          } else if (doc.type == 'faktur_pajak') {
            result.faktur_pajak = doc.url;
          } else if (doc.type == 'foto_bukti_tayang') {
            result.foto_bukti_tayang = doc.url;
          } else if (doc.type == 'signed_skp') {
            result.signed_skp = doc.url;
          } else if (doc.type == 'foto_buku_rekening') {
            result.foto_buku_rekening = doc.url;
          } else if (doc.type == 'payment_proof') {
            result.payment_proof = doc.url;
          } else if (doc.type == 'other') {
            result.other = [
              ...(result.other || []),
              { name: getFileNameFromUrl(doc.url, 'Other File'), url: doc.url },
            ];
          }
        });
      }

      await getTaxData(result);

      dispatch(setTempData(result.status));
      setDetailData(result);

      if (result?.skp_internal?.id) {
        const skpExternal = await getSkpExternalCount.request({
          params: {
            filter: {
              'skp_internal.id': result.skp_internal.id,
              'customer.id': result.customer.id,
            },
          },
        });
        setSkpExternalCount(skpExternal?.data?.data || 0);
      }
    }
  };

  const getTaxData = async (skpExternal) => {
    if (skpExternal.skp_internal?.coa_tax?.id) {
      const taxes = await getCoaTaxSettingsDetail.request(
        skpExternal.skp_internal.coa_tax.id,
      );
      if (taxes?.data?.data && Object.keys(taxes.data.data).length) {
        const coa_tax = taxes.data.data;
        let wht;
        if (
          skpExternal.skp_internal?.skp_type?.toLowerCase() == 'subdistributor'
        ) {
          wht = skpExternal.outlet?.wht;
        } else {
          wht = skpExternal.customer?.wht;
        }

        if (wht === 'npwp_badan') {
          const coaTaxWht = (coa_tax?.tax_corporate || [])
            .map((t) => ({
              ...t,
              id: t._id,
              label: formatCoaTaxLabel(t),
            }))
            .filter((t) => !t.is_deleted);

          setTaxesData(coaTaxWht);
          if (!coaTaxWht.find((t) => t.id == skpExternal.tax.id)) {
            skpExternal.tax = null;
            setIsInvalidTax(true);
          }
        } else if (wht === 'npwp_pribadi') {
          const coaTaxWht = (coa_tax?.tax_personal || [])
            .map((t) => ({
              ...t,
              id: t._id,
              label: formatCoaTaxLabel(t),
            }))
            .filter((t) => !t.is_deleted);
          setTaxesData(coaTaxWht);
          if (!coaTaxWht.find((t) => t.id == skpExternal.tax.id)) {
            skpExternal.tax = null;
            setIsInvalidTax(true);
          }
        } else {
          skpExternal.tax = null;
          setIsInvalidTax(true);
        }
        setVatsData(
          coa_tax.vat
            .map((t) => ({
              ...t,
              id: t._id,
              label: formatCoaTaxLabel(t),
            }))
            .filter((t) => !t.is_deleted),
        );
      }
    }
  };

  const getSkpInternalsBrandBudgets = async (
    id,
    customer_id,
    outlet_id = null,
  ) => {
    setSkpInternalsTableLoading(true);
    try {
      const filter = { 'skp_external.id': id, 'customer.id': customer_id };
      if (outlet_id) {
        filter['outlet.id'] = outlet_id;
      }
      const result = await getAllSkpExternalsBrandBudget.request({
        params: { filter },
      });
      if (result?.data?.data?.length) {
        setSkpInternalsBrandBugets(
          result.data.data.map((b) => ({
            ...b,
            initial_budget: b.initial_budget || b.budget_sample,
            initial_dpp: b.initial_dpp || b.dpp,
          })),
        );
      } else {
        setSkpInternalsBrandBugets([]);
      }
    } catch (error) {
      console.log('SKP External Brand And Nominal Budget Not Found', error);
    }
    setSkpInternalsTableLoading(false);
  };

  const changeStateToRejected = (reason) => {
    changeStatus(detailData?._id, 'rejected', reason);
  };

  const checkCalculation = async (status) => {
    if (isInvalidTax) {
      Toast.show({
        text1: 'Please reselect Tax (%)',
        type: 'error',
      });
      return;
    }
    // B2B-5936: MT & subdist cannot request confirmation with Nilai Aktivitas = 0
    if (
      status == 'need_approval' &&
      !isGt &&
      !(Number(detailData?.nilai_aktivitas) > 0)
    ) {
      Toast.show({
        text1: 'Please edit Actual Claim first. Nilai Aktivitas cannot be 0.',
        type: 'error',
      });
      return;
    }
    if (['joined', 'need_approval'].includes(status)) {
      try {
        dispatch(showPopup({ name: 'isLoading', value: true }));
        const claimTypeLabel =
          detailData.claim_type === 'partial'
            ? translate('Partial Claim')
            : translate('Full Claim');
        const msg = [
          `${translate(
            "You're about to submit this claim as a",
          )} ${claimTypeLabel}.`,
        ];
        if (skpInternalsBrandBugets?.length) {
          const calcRes = await MsOrder.checkSkpExternalCalculation({
            ...detailData,
            customer_brand_budgets: skpInternalsBrandBugets,
          });
          const calcData = calcRes?.data?.data;
          if (!calcData?.success) {
            msg.push(
              `${translate(
                'This claim exceeds the available budget by',
              )} ${currency(Math.abs(calcData.budget_remaining), true)}.`,
            );
          }
        }
        msg.push(translate('Are you sure you want to proceed?'));
        dispatch(showPopup({ name: 'isLoading', value: false }));
        dispatch(
          showAlert({
            show: true,
            title: 'Confirm Claim Submission',
            description: msg.join('\n'),
            onCallback: () => changeStatus(detailData?._id, status),
          }),
        );
      } catch (error) {
        console.log(error);
        dispatch(showPopup({ name: 'isLoading', value: false }));
        Toast.show({
          text1: error?.response?.data?.message || 'A system error occurred',
          type: 'error',
        });
      }
    }
  };

  const changeStatus = async (id, status, reason) => {
    if (status == 'need_approval' && !detailData?.reference_invoice_potong) {
      Toast.show({
        text1: 'Reference Invoice Potong is required',
        type: 'error',
      });
      dispatch(showPopup({ name: 'isLoading', value: false }));
      return;
    }
    if (
      (!detailData?.signed_skp || !detailData?.kwitansi) &&
      detailData?.status !== 'draft' &&
      status !== 'cancelled'
    ) {
      Toast.show({
        text1: 'Signed SKP and Kwitansi documents are required',
        type: 'error',
      });
      dispatch(showPopup({ name: 'isLoading', value: false }));
      return;
    }
    try {
      if (id) {
        dispatch(showPopup({ name: 'isLoading', value: true }));
        const payload = {};
        if (reason) {
          if (status == 'rejected') {
            payload.reason = reason;
          } else if (status == 'cancelled') {
            payload.reason_cancelled = reason;
          }
        }
        if (status === 'need_approval') {
          const wasRejected = detailData?.state_histories?.some(
            (h) => h.to_status === 'rejected',
          );
          if (wasRejected) {
            payload.is_resubmit = true;
          }
        }
        const requestConfirm = await MsOrder.changeStatusSkpExternal(
          id,
          status,
          payload,
        );
        if (requestConfirm && requestConfirm.data && requestConfirm.data.data) {
          dispatch(showPopup({ name: 'isLoading', value: false }));
          let title = '';
          let desc = '';
          if (status == 'need_approval') {
            title = 'SKP External Submitted';
            desc = 'SKP External submitted and need confirmation';
          } else if (status == 'cancelled') {
            title = 'SKP External Cancelled';
            desc = 'SKP External has been cancelled';
          } else if (status == 'approved') {
            title = 'SKP External Confirmed';
            desc = 'SKP External has been confirmed';
          } else if (status == 'draft') {
            title = 'SKP External Submitted';
            desc = 'SKP External has been drafted';
          } else if (status == 'rejected') {
            title = 'SKP External Rejected';
            desc = 'SKP External has been rejected';
          } else if (status == 'joined') {
            title = 'SKP External Customer Joined';
            desc = 'SKP External has been Customer Joined';
          } else if (status == 'completed') {
            title = 'SKP External Completed';
            desc = 'SKP External has been completed';
          }
          setModalReject(false);
          dispatch(
            showAlert({
              show: true,
              title: title,
              description: desc,
              isConfirm: false,
              onCallback: () => {
                getDetail(props.route.params.id);
              },
            }),
          );
        } else {
          dispatch(showPopup({ name: 'isLoading', value: false }));
          Toast.show({ text1: 'A system error occurred', type: 'error' });
        }
      }
      setAfterAction(true);
    } catch (error) {
      dispatch(showPopup({ name: 'isLoading', value: false }));
      dispatch(
        showAlert({
          show: true,
          title: 'Error',
          description:
            error?.response?.data?.message || 'A system error occurred',
          isConfirm: false,
        }),
      );
    }
    setModalReject(false);
    setModalCancel(false);
    setModalDelete(false);
  };

  const removeSkp = async (id, reason) => {
    try {
      if (id) {
        dispatch(showPopup({ name: 'isLoading', value: true }));
        const payload = {
          deleted_reason: reason,
          deleted_by: {
            id: userData?._id || userData?.id,
            name: userData?.name || '',
          },
          deleted_date: new Date().toISOString(),
        };
        const requestDelete = await MsOrder.deleteSkpExternal(id, payload);
        if (requestDelete?.status == 200) {
          dispatch(showPopup({ name: 'isLoading', value: false }));
          setModalDelete(false);
          dispatch(
            showAlert({
              show: true,
              title: 'Delete SKP External',
              description: 'SKP External has been successfully deleted',
              isConfirm: false,
              onCallback: () => {
                props.navigation.navigate('SkpExternalScreen');
              },
            }),
          );
        } else {
          dispatch(showPopup({ name: 'isLoading', value: false }));
          Toast.show({
            text1: 'A system error occurred',
            type: 'error',
          });
        }
      }
    } catch (error) {
      dispatch(showPopup({ name: 'isLoading', value: false }));
      Toast.show({
        text1: error?.response?.data?.message || 'A system error occurred',
        type: 'error',
      });
    }
  };

  const onExportPdf = async (id) => {
    dispatch(showPopup({ name: 'isLoading', value: true }));
    if (id) {
      try {
        await exportPdf('skp', id);
        dispatch(showPopup({ name: 'isLoading', value: false }));
      } catch (err) {
        dispatch(showPopup({ name: 'isLoading', value: false }));
        Toast.show({
          text1: 'A system error occurred',
          type: 'error',
        });
      }
    } else {
      dispatch(showPopup({ name: 'isLoading', value: false }));
      Toast.show({ text1: 'A system error occurred', type: 'error' });
    }
  };

  const deleteFile = (id, value) => {
    dispatch(
      showAlert({
        show: true,
        title: 'Remove Document',
        description: 'This document will be deleted. Do you want to proceed?',
        onCallback: async () => {
          await MsOrder.deleteSkpExternalAttachment(id, value);
          setDetailData({ ...detailData, [value]: '' });
        },
      }),
    );
  };

  const saveFile = async (id, payload) => {
    if (payload.url) {
      await MsOrder.updateSkpExternalAttachment(id, payload);
      getDetail(id);
    }
  };

  const deleteOtherFile = async (id, url) => {
    await MsOrder.deleteSkpExternalAttachmentByUrl(id, { url });
    getDetail(id);
  };

  const recalculateDpp = (skp, budget, customer, outlet, tax) => {
    const source = budget.budget_sample;
    let dpp = source;
    let isPkp = customer.is_pkp;
    if (skp?.skp_type?.toLowerCase() == 'subdistributor') {
      isPkp = outlet.is_pkp;
    }
    if (!isPkp) {
      const percentage = tax.tax_value;

      customer.pph_percentage = percentage;
      dpp = Math.round(source / (1 - percentage / 100));
    }
    return dpp || 0;
  };

  const handleChangeTax = async (value) => {
    dispatch(showPopup({ name: 'isLoading', value: true }));

    try {
      const budgetFinal = (skpInternalsBrandBugets || []).map((b) => {
        delete b.skp_internal;
        delete b._id;
        delete b.created_at;
        delete b.updated_at;

        b.dpp = recalculateDpp(
          detailData.skp_internal,
          b,
          detailData.customer,
          detailData.outlet,
          value,
        );
        b.skp_external = {
          id: detailData._id,
        };

        return b;
      });

      await MsOrder.updateOnlySkpExternal(detailData._id, {
        customer: detailData.customer,
        outlet: detailData.outlet,
        skp_internal: detailData.skp_internal,
        customer_brand_budgets: budgetFinal,
        grossup_pph: detailData.grossup_pph,
        nilai_aktivitas: detailData.nilai_aktivitas,
        pph: detailData.pph,
        total_biaya: detailData.total_biaya,
        total_nett: detailData.total_nett,
        status: detailData.status,
        vat: detailData.vat,
        tax: {
          ...value,
          id: value.id || value._id,
        },
      });

      const payload = {
        skp_external_id: detailData._id,
        customer_id: detailData.customer.id,
        outlet_id: detailData.outlet?.id,
        data: budgetFinal,
      };
      await MsOrder.createSkpExternalsBrandBudgetBulk(detailData._id, payload);

      Toast.show({
        text1: 'Tax updated successfully',
        type: 'success',
      });

      getDetail(detailData._id);
    } catch (error) {
      Toast.show({ text1: 'A system error occurred', type: 'error' });
      console.error(error);
    } finally {
      dispatch(showPopup({ name: 'isLoading', value: false }));
    }
  };

  const handleChangeVat = async (value) => {
    dispatch(showPopup({ name: 'isLoading', value: true }));

    try {
      const budgetFinal = (skpInternalsBrandBugets || []).map((b) => {
        delete b.skp_internal;
        delete b._id;
        delete b.created_at;
        delete b.updated_at;
        b.skp_external = {
          id: detailData._id,
        };

        return b;
      });

      await MsOrder.updateOnlySkpExternal(detailData._id, {
        customer: detailData.customer,
        outlet: detailData.outlet,
        skp_internal: detailData.skp_internal,
        customer_brand_budgets: budgetFinal,
        grossup_pph: detailData.grossup_pph,
        nilai_aktivitas: detailData.nilai_aktivitas,
        pph: detailData.pph,
        total_biaya: detailData.total_biaya,
        total_nett: detailData.total_nett,
        status: detailData.status,
        tax: detailData.tax,
        vat: {
          ...value,
          id: value.id || value._id,
        },
      });

      Toast.show({
        text1: 'VAT updated successfully',
        type: 'success',
      });

      getDetail(detailData._id);
    } catch (error) {
      Toast.show({ text1: 'A system error occurred', type: 'error' });
      console.error(error);
    } finally {
      dispatch(showPopup({ name: 'isLoading', value: false }));
    }
  };

  const from = props?.route?.params?.name;
  const goBack = () => {
    const isFromPromotionAgreementExternalList =
      props?.route?.params?.fromPromotionAgreementExternalList;

    if (isFromPromotionAgreementExternalList) {
      const parentNavigation =
        typeof props.navigation?.getParent === 'function'
          ? props.navigation.getParent()
          : null;

      if (parentNavigation) {
        parentNavigation.navigate('SkpInternalNavigator');
      } else {
        props.navigation?.navigate('SkpInternalNavigator');
      }
      return;
    }

    if (afterAction) {
      setAfterAction(false);

      props.navigation.navigate('SkpExternalScreen');
      return;
    }

    if (from === 'SKP External Approval') {
      props.navigation.navigate('TaskNotificationNavigator', {
        screen: 'TaskNotificationScreen',
        params: {
          task: true,
        },
      });
      return;
    }

    props.navigation.replace('SkpExternalDetailScreen', {});
    props.navigation.navigate('SkpExternalScreen');
  };

  const handleGoToSkpExternal = () => {
    if (detailData?.reference_code) {
      props.navigation.navigate('SkpExternalListScreen', {
        id: detailData.skp_internal.id,
        isFromExternal: true,
        skpExternalId: detailData._id,
        customerId: detailData.customer.id,
      });
    }
  };

  useEffect(() => {
    let mounted = true;
    if (mounted) {
      dispatch(setTempData(null));
      if (props?.route?.params?.refresh) {
        // If refresh param exists, just refresh data without resetting detailData
        if (props?.route?.params?.id) {
          getDetail(props.route.params.id);
        }
      } else {
        setDetailData({});
        if (props?.route?.params?.id) {
          getDetail(props.route.params.id);
        }
      }
    }
    return () => {
      mounted = false;
    };
  }, [props]);

  // The header's comment entry point lives in SkpExternalNavigator (it shares the header
  // with the Cancel/Delete "..." menu, which needs navigator-level state). Bridge the
  // button press back into this screen's drawer state via the route params it sets.
  useEffect(() => {
    if (props.route.params?.showComments) {
      setShowComments(true);
      props.navigation.setParams({ showComments: undefined });
    }
  }, [props.route.params?.showComments]);

  const joinedReminderDays = getSkpJoinedReminderDays(detailData);

  return (
    <Layout.Container testID="skp-external-detail-screen">
      {getSkpExternalDetail?.loading ? (
        <Layout.Wrapper>
          <Layout.Loader />
        </Layout.Wrapper>
      ) : detailData && Object.keys(detailData).length ? (
        <>
          {isWeb ? (
            <Layout.Wrapper white={false}>
              <Layout.Back
                testID="skp-external-detail-back"
                title={
                  from === 'SKP External Approval' ? 'Task' : 'Skp External'
                }
                onCallback={() => goBack()}
              />
            </Layout.Wrapper>
          ) : (
            false
          )}
          <MobileScroll>
            <Layout.Wrapper rounded>
              {joinedReminderDays != null ? (
                <ReminderNotice
                  banner
                  testID="skp-external-detail-joined-reminder"
                  title="SKP action required."
                  description={`${joinedReminderDays} ${translate(
                    'days since joined. Please proceed and request your confirmation data.',
                  )}`}
                  actionLabel="Proceed SKP"
                  onAction={() => checkCalculation('need_approval')}
                />
              ) : (
                false
              )}
              <View style={styles.sectionWrapper}>
                {isWeb ? (
                  <View
                    style={[
                      styles.rowContainer,
                      {
                        position: 'relative',
                        zIndex: 1,
                        justifyContent: 'space-between',
                      },
                    ]}>
                    <SociollaText
                      testID="skp-external-detail-title"
                      type="medium"
                      style={{ fontSize: 20 }}>
                      {detailData?.reference_code || ''}
                    </SociollaText>
                    <View style={{ flexDirection: 'row' }}>
                      {showEditButton && detailData?.status == 'draft' ? (
                        <Button
                          testID="skp-external-detail-customer-joined-btn"
                          type="primary"
                          rounded
                          buttonStyle={{
                            width: 160,
                            marginRight: 10,
                            backgroundColor: Colors.blue0,
                          }}
                          title={
                            <SociollaText
                              type="bold"
                              style={{ color: Colors.white }}>
                              Customer Joined
                            </SociollaText>
                          }
                          onPress={() => checkCalculation('joined')}
                        />
                      ) : (
                        false
                      )}
                      {(showEditButton && detailData?.status == 'draft') ||
                      detailData?.status == 'joined' ? (
                        <View style={{ marginRight: 10 }}>
                          <Button
                            testID="skp-external-detail-request-confirm-btn"
                            type="primary"
                            rounded
                            buttonStyle={{
                              width: 185,
                              marginRight: 10,
                              backgroundColor: Colors.blue0,
                            }}
                            disabled={detailData?.status == 'draft'}
                            title={
                              <SociollaText
                                type="bold"
                                style={{ color: Colors.white }}>
                                Request Confirmation
                              </SociollaText>
                            }
                            onPress={() => checkCalculation('need_approval')}
                          />
                        </View>
                      ) : (
                        false
                      )}
                      {showEditButton &&
                      detailData?.status != 'need_approval' &&
                      detailData?.status != 'approved' &&
                      detailData?.status != 'cancelled' &&
                      detailData?.status != 'completed' ? (
                        <Button
                          testID="skp-external-detail-edit-btn"
                          type="primary"
                          rounded
                          buttonStyle={{ width: 80, marginRight: 10 }}
                          title={
                            <SociollaText
                              type="bold"
                              style={{ color: Colors.white }}>
                              Edit
                            </SociollaText>
                          }
                          onPress={() =>
                            props.navigation.replace('SkpExternalFormScreen', {
                              id: detailData._id,
                              name: detailData.name
                                ? detailData.reference_code
                                : '-',
                              titleScreen: detailData.reference_code
                                ? detailData.reference_code
                                : '-',
                            })
                          }
                        />
                      ) : (
                        false
                      )}
                      {showCompletedButton &&
                      detailData?.status == 'approved' ? (
                        <Button
                          testID="skp-external-detail-completed-btn"
                          type="primary"
                          rounded
                          buttonStyle={{
                            width: 185,
                            marginRight: 10,
                            backgroundColor: Colors.blue0,
                          }}
                          title={
                            <SociollaText
                              type="bold"
                              style={{ color: Colors.white }}>
                              Completed
                            </SociollaText>
                          }
                          onPress={() =>
                            changeStatus(detailData?._id, 'completed')
                          }
                        />
                      ) : (
                        false
                      )}
                      {showCancelButton &&
                      [
                        'draft',
                        'joined',
                        'rejected',
                        'approved',
                        'completed',
                      ].includes(detailData?.status) ? (
                        <Button
                          testID="skp-external-detail-cancel-btn"
                          type="primary"
                          rounded
                          buttonStyle={{ width: 80, marginRight: 10 }}
                          title={
                            <SociollaText
                              type="bold"
                              style={{ color: Colors.white }}>
                              Cancel
                            </SociollaText>
                          }
                          onPress={() => {
                            dispatch(
                              showAlert({
                                show: true,
                                title: 'Cancel SKP External',
                                description:
                                  'This SKP External will be cancelled. Do you want to proceed?',
                                onCallback: () => {
                                  setModalCancel(true);
                                },
                              }),
                            );
                          }}
                        />
                      ) : (
                        false
                      )}
                      {detailData?.status != 'cancelled' && !canApprove() ? (
                        <Button
                          testID="skp-external-detail-pdf-btn"
                          type="primary"
                          rounded
                          buttonStyle={{ width: 42, marginRight: 10 }}
                          title={
                            <Image
                              style={{
                                width: 13,
                                height: 17,
                              }}
                              width={13}
                              height={17}
                              source={require('@assets/img/icons/pdf.png')}
                            />
                          }
                          onPress={() => onExportPdf(detailData._id)}
                        />
                      ) : (
                        false
                      )}
                      {showDeleteButton &&
                      (detailData?.status == 'draft' ||
                        detailData?.status == 'joined') ? (
                        <Button
                          testID="skp-external-detail-delete-btn"
                          type="primary"
                          rounded
                          buttonStyle={{
                            width: 42,
                            marginRight: 10,
                            backgroundColor: Colors.blue1,
                          }}
                          title={
                            <Image
                              style={{
                                width: 13,
                                height: 17,
                              }}
                              width={13}
                              height={17}
                              source={require('@assets/img/icons/trash-white.svg')}
                              svg={true}
                            />
                          }
                          onPress={() => {
                            dispatch(
                              showAlert({
                                show: true,
                                title: 'Delete SKP External',
                                description:
                                  'This SKP External will be deleted. Do you want to proceed?',
                                onCallback: () => {
                                  setModalDelete(true);
                                },
                              }),
                            );
                          }}
                        />
                      ) : (
                        false
                      )}
                      {canApprove() ? (
                        <View style={{ flexDirection: 'row' }}>
                          <Button
                            testID="skp-external-detail-reject-btn"
                            type="primary"
                            rounded
                            buttonStyle={{
                              width: 150,
                              marginRight: 10,
                              backgroundColor: Colors.white,
                            }}
                            title={
                              <SociollaText type="bold" style={{}}>
                                Reject
                              </SociollaText>
                            }
                            onPress={() => setModalReject(true)}
                          />
                          <Button
                            testID="skp-external-detail-approve-btn"
                            type="primary"
                            rounded
                            buttonStyle={{
                              width: 150,
                              marginRight: 10,
                              backgroundColor: Colors.blue0,
                            }}
                            title={
                              <SociollaText
                                type="bold"
                                style={{ color: Colors.white }}>
                                Approve
                              </SociollaText>
                            }
                            onPress={() =>
                              changeStatus(detailData?._id, 'approved')
                            }
                          />
                        </View>
                      ) : (
                        false
                      )}
                      {detailData?.status != 'cancelled' && canApprove() ? (
                        <Button
                          testID="skp-external-detail-approver-pdf-btn"
                          type="primary"
                          rounded
                          buttonStyle={{ width: 42, marginRight: 10 }}
                          title={
                            <Image
                              style={{
                                width: 13,
                                height: 17,
                              }}
                              width={13}
                              height={17}
                              source={require('@assets/img/icons/pdf.png')}
                            />
                          }
                          onPress={() => onExportPdf(detailData._id)}
                        />
                      ) : (
                        false
                      )}
                    </View>
                  </View>
                ) : (
                  false
                )}
                <View
                  style={[
                    { maxWidth: isWeb ? 400 : '100%' },
                    isWeb ? { paddingVertical: 15 } : { paddingBottom: 20 },
                  ]}>
                  <ItemDetail
                    testID="skp-external-detail-skp-reference"
                    name="SKP Reference"
                    value={
                      detailData.skp_internal?.reference_code
                        ? detailData.skp_internal.reference_code
                        : '-'
                    }
                    valueStyle={{ color: Colors.blue0 }}
                    onPress={() => {
                      if (detailData.skp_internal?.id) {
                        props.navigation.navigate('SkpInternalNavigator', {
                          screen: 'SkpInternalDetailScreen',
                          params: {
                            titleScreen: detailData.skp_internal?.name || '-',
                            name: detailData.skp_internal?.name || '-',
                            id: detailData.skp_internal?.id,
                            go_back_key: props.route?.key,
                          },
                        });
                      }
                    }}
                  />
                  <ItemDetail
                    testID="skp-external-detail-skp-name"
                    name="SKP Reference Name"
                    value={
                      detailData.skp_internal?.name
                        ? detailData.skp_internal.name
                        : '-'
                    }
                  />
                  <ItemDetail
                    testID="skp-external-detail-period"
                    name="SKP Reference Periode"
                    centered
                    value={
                      detailData.from_date || detailData.to_date
                        ? `${
                            detailData.from_date
                              ? parseDate(detailData.from_date)
                              : '-'
                          } - ${
                            detailData.to_date
                              ? parseDate(detailData.to_date)
                              : '-'
                          }`
                        : '-'
                    }
                  />
                  <ItemDetail
                    testID="skp-external-detail-related-promo"
                    name="Related Promotion Agreement"
                    centered
                    valueComponent={
                      <View
                        style={{ flexDirection: 'row', alignItems: 'center' }}>
                        <SociollaText
                          style={{
                            fontSize: !isNative ? 12 : 13,
                            marginLeft: 20,
                            marginRight: 20,
                          }}>
                          {skpExternalCount}
                        </SociollaText>
                        {skpExternalCount > 0 && (
                          <Button
                            testID="skp-external-detail-see-all-btn"
                            type="primary"
                            rounded={true}
                            buttonStyle={{
                              minWidth: 100,
                              paddingHorizontal: 12,
                              backgroundColor: Colors.white,
                            }}
                            title={
                              <View
                                style={{
                                  flexDirection: 'row',
                                  alignItems: 'center',
                                }}>
                                <FontAwesome
                                  name="eye"
                                  size={12}
                                  color={Colors.black}
                                  style={{ marginRight: 8 }}
                                />

                                <SociollaText
                                  type="medium"
                                  style={{ fontSize: 14, color: Colors.black }}>
                                  See All
                                </SociollaText>
                              </View>
                            }
                            onPress={handleGoToSkpExternal}
                          />
                        )}
                      </View>
                    }
                  />
                  <ItemDetail
                    testID="skp-external-detail-status"
                    name="Status"
                    centered
                    valueComponent={
                      <View
                        style={{
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'center',
                        }}>
                        <StatusBadge status={detailData.status} />
                        <TouchableOpacity
                          testID="skp-external-detail-see-log-btn"
                          onPress={() => setShowModalLog(true)}>
                          <SociollaText
                            style={{
                              fontSize: 12,
                              color: Colors.blue0,
                              marginLeft: 20,
                              zIndex: 99,
                            }}>
                            see log
                          </SociollaText>
                        </TouchableOpacity>
                      </View>
                    }
                  />
                  {detailData?.status == 'need_approval' && (
                    <ItemDetail
                      testID="skp-external-detail-waiting-approval"
                      name="Waiting Approval"
                      withoutBorder={true}
                      valueComponent={
                        <View style={{ flexDirection: 'column' }}>
                          {detailData.waiting_approvals?.length
                            ? detailData.waiting_approvals.map(
                                (appr, index) => (
                                  <User
                                    name={appr.name || ''}
                                    image={appr.image || ''}
                                    key={'list-user-approval-' + index}
                                    index={index}
                                    style={{ paddingBottom: 10 }}
                                  />
                                ),
                              )
                            : false}
                        </View>
                      }
                    />
                  )}
                  {detailData?.status == 'rejected' ? (
                    <ItemDetail
                      testID="skp-external-detail-rejected-by"
                      name="Rejected By"
                      centered
                      value={
                        detailData.state_histories?.length
                          ? detailData.state_histories[
                              detailData.state_histories.length - 1
                            ].status_changed_by?.name
                          : '-'
                      }
                    />
                  ) : (
                    false
                  )}
                  {detailData?.status == 'rejected' ? (
                    <ItemDetail
                      testID="skp-external-detail-rejected-notes"
                      name="Notes"
                      centered
                      value={
                        detailData.state_histories?.length
                          ? detailData.state_histories[
                              detailData.state_histories.length - 1
                            ].reason
                          : '-'
                      }
                    />
                  ) : (
                    false
                  )}
                  {detailData?.status == 'cancelled' &&
                  detailData?.cancellation_info ? (
                    <ItemDetail
                      testID="skp-external-detail-cancelled-by"
                      name="Cancelled By"
                      centered
                      value={
                        detailData.cancellation_info.cancelled_by?.name || '-'
                      }
                    />
                  ) : (
                    false
                  )}
                  {detailData?.status == 'cancelled' &&
                  detailData?.cancellation_info ? (
                    <ItemDetail
                      testID="skp-external-detail-cancelled-notes"
                      name="Notes"
                      centered
                      value={
                        detailData.cancellation_info.reason_cancelled || '-'
                      }
                    />
                  ) : (
                    false
                  )}
                  <ItemDetail
                    testID="skp-external-detail-coa"
                    name="COA Activity Promo"
                    centered
                    value={
                      detailData.skp_internal?.coa_tax?.cost_type
                        ? detailData.skp_internal.coa_tax.cost_type
                        : '-'
                    }
                  />
                  <ItemDetail
                    testID="skp-external-detail-tax"
                    name="Tax (%)"
                    centered
                    valueComponent={
                      <>
                        {canEditTax ? (
                          <SelectBox
                            hideLabel
                            hideSearch
                            options={taxesData}
                            value={detailData?.tax}
                            placeholder="Select Tax (%)"
                            trackBy="id"
                            labelKey="label"
                            formLabelKey="label"
                            isRequired
                            onChange={handleChangeTax}
                          />
                        ) : (
                          <SociollaText
                            style={{
                              width: !isNative
                                ? 'calc(100% - 130px)'
                                : fullWidth - 160,
                              fontSize: !isNative ? 12 : 13,
                              marginLeft: 20,
                            }}>
                            {detailData.tax
                              ? formatCoaTaxLabel(detailData.tax)
                              : '-'}
                          </SociollaText>
                        )}
                      </>
                    }
                  />
                  <ItemDetail
                    testID="skp-external-detail-vat"
                    name="VAT (%)"
                    centered
                    valueComponent={
                      <>
                        {canEditTax ? (
                          <SelectBox
                            hideLabel
                            hideSearch
                            options={vatsData}
                            value={detailData?.vat}
                            placeholder="Select VAT (%)"
                            trackBy="id"
                            labelKey="label"
                            formLabelKey="label"
                            isRequired
                            onChange={handleChangeVat}
                          />
                        ) : (
                          <SociollaText
                            style={{
                              width: !isNative
                                ? 'calc(100% - 130px)'
                                : fullWidth - 160,
                              fontSize: !isNative ? 12 : 13,
                              marginLeft: 20,
                            }}>
                            {detailData.vat
                              ? formatCoaTaxLabel(detailData.vat)
                              : '-'}
                          </SociollaText>
                        )}
                      </>
                    }
                  />
                  <ItemDetail
                    testID="skp-external-detail-customer-name"
                    name="Customer Name"
                    centered
                    value={
                      detailData.customer?.name ? detailData.customer.name : '-'
                    }
                    valueStyle={{ color: Colors.blue0 }}
                    onPress={() => {
                      props.navigation.navigate('CustomerNavigator', {
                        screen: 'CustomerDetailScreen',
                        params: {
                          id: detailData.customer?.id,
                          skp_id: detailData.id,
                          name: detailData.customer?.name,
                          prev_page: 'skp-external',
                          reference_code: detailData.reference_code,
                        },
                      });
                    }}
                  />
                  <ItemDetail
                    testID="skp-external-detail-customer-code"
                    name="Customer Code"
                    centered
                    value={
                      detailData.customer?.code ? detailData.customer.code : '-'
                    }
                  />
                  <ItemDetail
                    testID="skp-external-detail-outlet-name"
                    name="Outlet Name"
                    centered
                    value={
                      detailData.outlet?.name ? detailData.outlet.name : '-'
                    }
                    valueStyle={{ color: Colors.blue0 }}
                    onPress={() => {
                      props.navigation.navigate('SubdistributorNavigator', {
                        screen: 'SubdistributorDetailScreen',
                        params: {
                          id: detailData.outlet?.id,
                          skp_id: detailData.id,
                          name: detailData.outlet?.name,
                          prev_page: 'skp-external',
                          reference_code: detailData.reference_code,
                        },
                      });
                    }}
                  />
                  <ItemDetail
                    testID="skp-external-detail-outlet-code"
                    name="Outlet Code"
                    centered
                    value={
                      detailData.outlet?.code ? detailData.outlet.code : '-'
                    }
                  />
                  <ItemDetail
                    testID="skp-external-detail-business-type"
                    name="Business Type"
                    centered
                    value={
                      detailData.customer?.market_type?.business_type?.code
                        ? detailData.customer.market_type.business_type.code
                        : '-'
                    }
                  />
                  <ItemDetail
                    testID="skp-external-detail-sales-team"
                    name="Sales Team"
                    centered
                    value={
                      detailData.sales_team?.name
                        ? detailData.sales_team.name
                        : '-'
                    }
                  />
                  <RenderIf
                    condition={['mt', 'subdistributor'].includes(
                      detailData?.skp_internal?.skp_type?.toLowerCase(),
                    )}>
                    <ItemDetail
                      testID="skp-external-detail-claim-type"
                      name="Claim Type"
                      centered
                      value={
                        detailData.claim_type
                          ? detailData.claim_type == 'partial'
                            ? 'Partial Claim'
                            : 'Full Claim'
                          : '-'
                      }
                    />
                  </RenderIf>
                  {!isSkpType(detailData?.skp_internal, 'gt') ? (
                    <ItemDetail
                      testID="skp-external-detail-account-no"
                      name="SKP External Account Number"
                      centered
                      value={
                        detailData?.skp_external_account_no
                          ? detailData?.skp_external_account_no
                          : '-'
                      }
                    />
                  ) : (
                    false
                  )}
                  {!isSkpType(detailData?.skp_internal, 'gt') ? (
                    <ItemDetail
                      testID="skp-external-detail-kwitansi-no"
                      name="Invoice / Kwitansi Number"
                      centered
                      value={
                        detailData?.kwitansi_no ? detailData?.kwitansi_no : '-'
                      }
                    />
                  ) : (
                    false
                  )}
                  <ItemDetail
                    testID="skp-external-detail-ref-invoice"
                    name="Reference Invoice Potong"
                    centered
                    value={
                      detailData.reference_invoice_potong
                        ? detailData.reference_invoice_potong
                        : '-'
                    }
                  />
                  {!isWeb && skpInternalsBrandBugets.length > 0 && (
                    <ItemDetail
                      name="Brand & Nominal Budget"
                      centered
                      value={
                        <Button
                          testID="skp-external-detail-budget-view-btn"
                          type="primary"
                          rounded
                          buttonStyle={{ width: 80, marginRight: 10 }}
                          title={
                            <SociollaText
                              type="bold"
                              style={{ color: Colors.white }}>
                              View
                            </SociollaText>
                          }
                          onPress={() =>
                            props.navigation.navigate(
                              'SkpExternalBudgetTableScreen',
                              {
                                id: detailData._id,
                                customer_id: detailData.customer?.id,
                                outlet_id: detailData.outlet?.id,
                                skp_type: detailData.skp_internal?.skp_type,
                              },
                            )
                          }
                        />
                      }
                    />
                  )}
                  {isWeb && skpInternalsBrandBugets.length > 0 ? (
                    <ItemDetail
                      name="Brand & Nominal Budget"
                      centered
                      value={
                        <SkpInternalBrandBudgetTable
                          data={skpInternalsBrandBugets}
                          loading={skpInternalsTableLoading}
                          isGt={isGt}
                        />
                      }
                    />
                  ) : skpInternalsBrandBugets.length === 0 ? (
                    <ItemDetail
                      name="Brand & Nominal Budget"
                      centered
                      value={'-'}
                    />
                  ) : null}
                  <ItemDetail
                    testID="skp-external-detail-nilai-aktivitas"
                    name="Nilai Aktivitas"
                    centered
                    value={
                      detailData?.nilai_aktivitas
                        ? currency(detailData.nilai_aktivitas, true)
                        : currency(0, true)
                    }
                  />
                  <ItemDetail
                    testID="skp-external-detail-grossup-pph"
                    name="Grossup PPH"
                    centered
                    value={detailData.grossup_pph ? 'Yes' : 'No'}
                  />
                  <ItemDetail
                    testID="skp-external-detail-skp-balance"
                    name="SKP Balance Remaining"
                    centered
                    value={
                      detailData.skp_balance
                        ? currency(detailData.skp_balance, true)
                        : currency(0, true)
                    }
                  />
                  <ItemDetail
                    testID="skp-external-detail-total-biaya"
                    name="Total Biaya/DPP(coa exp)"
                    centered
                    value={
                      detailData.total_biaya
                        ? currency(detailData.total_biaya, true)
                        : currency(0, true)
                    }
                  />
                  <ItemDetail
                    testID="skp-external-detail-pph"
                    name="PPH(coa wht)"
                    centered
                    value={
                      detailData.pph
                        ? currency(detailData.pph, true)
                        : currency(0, true)
                    }
                  />
                  <ItemDetail
                    testID="skp-external-detail-ppn"
                    name="PPN(coa vat)"
                    centered
                    value={
                      detailData.ppn
                        ? currency(detailData.ppn, true)
                        : currency(0, true)
                    }
                  />
                  <ItemDetail
                    testID="skp-external-detail-total-nett"
                    name="Total Nett(coa ar)"
                    centered
                    value={
                      detailData.total_nett
                        ? currency(detailData.total_nett, true)
                        : currency(0, true)
                    }
                  />
                  <ItemDetail
                    testID="skp-external-detail-last-updated"
                    name="Last Updated, By"
                    centered
                    value={`${
                      detailData.updated_at
                        ? parseDate(detailData.updated_at)
                        : '-'
                    }, ${
                      detailData?.updated_by?.name
                        ? `, ${detailData.updated_by.name}`
                        : ''
                    }`}
                  />
                  <ItemDetail
                    testID="skp-external-detail-signed-skp"
                    name="Signed Skp"
                    nameComponent={
                      <View style={styles.row}>
                        <SociollaText style={styles.label}>
                          Signed Skp
                        </SociollaText>
                        <SociollaText style={styles.required}>*</SociollaText>
                      </View>
                    }
                    centered
                    value={
                      detailData.signed_skp ? (
                        <View
                          style={
                            isWeb
                              ? styles.webCardTextStyle
                              : styles.mobileCardTextStyle
                          }>
                          <FileInput
                            value={getFileNameFromUrl(
                              detailData.signed_skp,
                              'Signed Skp',
                            )}
                            showTrash={false}
                            containerStyleExtra={[
                              !isWeb
                                ? styles.mobileUploadForm
                                : styles.webUploadForm,
                              isWeb ? {} : styles.textStyleFix,
                            ]}
                          />
                          <TouchableOpacity
                            style={styles.uploadImageContainer}
                            onPress={() =>
                              downloadFile(
                                detailData.signed_skp,
                                'SignedSkp',
                                '',
                                true,
                              )
                            }>
                            <Image
                              style={{
                                width: 32,
                                height: 32,
                                marginRight: isWeb ? 100 : 0,
                              }}
                              width={32}
                              height={32}
                              isResolveAsset
                              source={require('@/assets/img/icons/attachment.png')}
                            />
                          </TouchableOpacity>
                          {showEditButton && (
                            <TouchableOpacity
                              style={styles.deleteButton}
                              onPress={() =>
                                deleteFile(detailData._id, 'signed_skp')
                              }>
                              <Image
                                style={{
                                  width: 20,
                                  height: 20,
                                  marginLeft: isWeb ? 10 : 0,
                                  marginRight: 100,
                                }}
                                width={20}
                                height={20}
                                svg={true}
                                source={require('@assets/img/icons/close-circle-blue.svg')}
                              />
                            </TouchableOpacity>
                          )}
                        </View>
                      ) : showEditButton ? (
                        <Layout.FormGroup>
                          <FileInput
                            confirmRemove
                            value={getFileNameFromUrl(
                              detailData.signed_skp,
                              'Signed Skp',
                            )}
                            onChange={(value) => {
                              saveFile(detailData._id, {
                                type: 'signed_skp',
                                url: value,
                              });
                            }}
                            containerStyleExtra={[
                              !isWeb
                                ? styles.mobileUploadForm
                                : styles.webUploadForm,
                              isWeb ? {} : styles.textStyleFix,
                            ]}
                          />
                        </Layout.FormGroup>
                      ) : (
                        '-'
                      )
                    }
                  />
                  <ItemDetail
                    testID="skp-external-detail-kwitansi"
                    nameComponent={
                      <View style={styles.row}>
                        <SociollaText style={styles.label}>
                          Kwitansi
                        </SociollaText>
                        <SociollaText style={styles.required}>*</SociollaText>
                      </View>
                    }
                    centered
                    value={
                      detailData?.kwitansi ? (
                        <View
                          style={
                            isWeb
                              ? styles.webCardTextStyle
                              : styles.mobileCardTextStyle
                          }>
                          <FileInput
                            value={getFileNameFromUrl(
                              detailData.kwitansi,
                              'Kwitansi',
                            )}
                            showTrash={false}
                            containerStyleExtra={[
                              !isWeb
                                ? styles.mobileUploadForm
                                : styles.webUploadForm,
                              isWeb ? {} : styles.textStyleFix,
                            ]}
                          />
                          <TouchableOpacity
                            style={styles.uploadImageContainer}
                            onPress={() =>
                              downloadFile(
                                detailData.kwitansi,
                                'Kwitansi',
                                '',
                                true,
                              )
                            }>
                            <Image
                              style={{
                                width: 32,
                                height: 32,
                                marginRight: isWeb ? 100 : 0,
                              }}
                              width={32}
                              height={32}
                              isResolveAsset
                              source={require('@/assets/img/icons/attachment.png')}
                            />
                          </TouchableOpacity>
                          {showEditButton && (
                            <TouchableOpacity
                              style={styles.deleteButton}
                              onPress={() =>
                                deleteFile(detailData._id, 'kwitansi')
                              }>
                              <Image
                                style={{
                                  width: 20,
                                  height: 20,
                                  marginLeft: isWeb ? 10 : 0,
                                  marginRight: 100,
                                }}
                                width={20}
                                height={20}
                                svg={true}
                                source={require('@assets/img/icons/close-circle-blue.svg')}
                              />
                            </TouchableOpacity>
                          )}
                        </View>
                      ) : showEditButton ? (
                        <Layout.FormGroup>
                          <FileInput
                            confirmRemove
                            value={getFileNameFromUrl(
                              detailData.kwitansi,
                              'Kwitansi',
                            )}
                            onChange={(value) => {
                              saveFile(detailData._id, {
                                type: 'kwitansi',
                                url: value,
                              });
                            }}
                            containerStyleExtra={[
                              !isWeb
                                ? styles.mobileUploadForm
                                : styles.webUploadForm,
                              isWeb ? {} : styles.textStyleFix,
                            ]}
                          />
                        </Layout.FormGroup>
                      ) : (
                        '-'
                      )
                    }
                  />
                  <ItemDetail
                    testID="skp-external-detail-faktur-pajak"
                    nameComponent={
                      <SociollaText style={styles.label}>
                        Faktur Pajak
                      </SociollaText>
                    }
                    centered
                    value={
                      detailData.faktur_pajak ? (
                        <View
                          style={
                            isWeb
                              ? styles.webCardTextStyle
                              : styles.mobileCardTextStyle
                          }>
                          <FileInput
                            value={getFileNameFromUrl(
                              detailData.faktur_pajak,
                              'Faktur Pajak',
                            )}
                            showTrash={false}
                            containerStyleExtra={[
                              !isWeb
                                ? styles.mobileUploadForm
                                : styles.webUploadForm,
                              isWeb ? {} : styles.textStyleFix,
                            ]}
                          />
                          <TouchableOpacity
                            style={styles.uploadImageContainer}
                            onPress={() =>
                              downloadFile(
                                detailData.faktur_pajak,
                                'fakturPajak',
                                '',
                                true,
                              )
                            }>
                            <Image
                              style={{
                                width: 32,
                                height: 32,
                                marginRight: isWeb ? 100 : 0,
                              }}
                              width={32}
                              height={32}
                              isResolveAsset
                              source={require('@/assets/img/icons/attachment.png')}
                            />
                          </TouchableOpacity>
                          {showEditButton && (
                            <TouchableOpacity
                              style={styles.deleteButton}
                              onPress={() =>
                                deleteFile(detailData._id, 'faktur_pajak')
                              }>
                              <Image
                                style={{
                                  width: 20,
                                  height: 20,
                                  marginLeft: isWeb ? 10 : 0,
                                  marginRight: 100,
                                }}
                                width={20}
                                height={20}
                                svg={true}
                                source={require('@assets/img/icons/close-circle-blue.svg')}
                              />
                            </TouchableOpacity>
                          )}
                        </View>
                      ) : showEditButton ? (
                        <Layout.FormGroup>
                          <FileInput
                            confirmRemove
                            value={getFileNameFromUrl(
                              detailData.faktur_pajak,
                              'Faktur Pajak',
                            )}
                            onChange={(value) => {
                              saveFile(detailData._id, {
                                type: 'faktur_pajak',
                                url: value,
                              });
                            }}
                            containerStyleExtra={[
                              !isWeb
                                ? styles.mobileUploadForm
                                : styles.webUploadForm,
                              isWeb ? {} : styles.textStyleFix,
                            ]}
                          />
                        </Layout.FormGroup>
                      ) : (
                        '-'
                      )
                    }
                  />
                  <ItemDetail
                    testID="skp-external-detail-foto-bukti-tayang"
                    nameComponent={
                      <SociollaText style={styles.label}>
                        Foto Bukti Tayang
                      </SociollaText>
                    }
                    centered
                    value={
                      detailData.foto_bukti_tayang ? (
                        <View
                          style={
                            isWeb
                              ? styles.webCardTextStyle
                              : styles.mobileCardTextStyle
                          }>
                          <FileInput
                            value={getFileNameFromUrl(
                              detailData.foto_bukti_tayang,
                              'Foto Bukti Tayang',
                            )}
                            showTrash={false}
                            containerStyleExtra={[
                              !isWeb
                                ? styles.mobileUploadForm
                                : styles.webUploadForm,
                              isWeb ? {} : styles.textStyleFix,
                            ]}
                          />
                          <TouchableOpacity
                            style={styles.uploadImageContainer}
                            onPress={() =>
                              downloadFile(
                                detailData.foto_bukti_tayang,
                                'fotoBuktiTayang',
                                '',
                                true,
                              )
                            }>
                            <Image
                              style={{
                                width: 32,
                                height: 32,
                                marginRight: isWeb ? 100 : 0,
                              }}
                              width={32}
                              height={32}
                              isResolveAsset
                              source={require('@/assets/img/icons/attachment.png')}
                            />
                          </TouchableOpacity>
                          {showEditButton && (
                            <TouchableOpacity
                              style={styles.deleteButton}
                              onPress={() =>
                                deleteFile(detailData._id, 'foto_bukti_tayang')
                              }>
                              <Image
                                style={{
                                  width: 20,
                                  height: 20,
                                  marginLeft: isWeb ? 10 : 0,
                                  marginRight: 100,
                                }}
                                width={20}
                                height={20}
                                svg={true}
                                source={require('@assets/img/icons/close-circle-blue.svg')}
                              />
                            </TouchableOpacity>
                          )}
                        </View>
                      ) : showEditButton ? (
                        <Layout.FormGroup>
                          <FileInput
                            confirmRemove
                            value={getFileNameFromUrl(
                              detailData.foto_bukti_tayang,
                              'Foto Bukti Tayang',
                            )}
                            onChange={(value) => {
                              saveFile(detailData._id, {
                                type: 'foto_bukti_tayang',
                                url: value,
                              });
                            }}
                            containerStyleExtra={[
                              !isWeb
                                ? styles.mobileUploadForm
                                : styles.webUploadForm,
                              isWeb ? {} : styles.textStyleFix,
                            ]}
                          />
                        </Layout.FormGroup>
                      ) : (
                        '-'
                      )
                    }
                  />
                  <ItemDetail
                    testID="skp-external-detail-foto-buku-rekening"
                    nameComponent={
                      <SociollaText style={styles.label}>
                        Foto Buku Rekening
                      </SociollaText>
                    }
                    centered
                    value={
                      detailData.foto_buku_rekening ? (
                        <View
                          style={
                            isWeb
                              ? styles.webCardTextStyle
                              : styles.mobileCardTextStyle
                          }>
                          <FileInput
                            value={getFileNameFromUrl(
                              detailData.foto_buku_rekening,
                              'Foto Buku Rekening',
                            )}
                            showTrash={false}
                            containerStyleExtra={[
                              !isWeb
                                ? styles.mobileUploadForm
                                : styles.webUploadForm,
                              isWeb ? {} : styles.textStyleFix,
                            ]}
                          />
                          <TouchableOpacity
                            style={styles.uploadImageContainer}
                            onPress={() =>
                              downloadFile(
                                detailData.foto_buku_rekening,
                                'FotoBukuRekening',
                                '',
                                true,
                              )
                            }>
                            <Image
                              style={{
                                width: 32,
                                height: 32,
                                marginRight: isWeb ? 100 : 0,
                              }}
                              width={32}
                              height={32}
                              isResolveAsset
                              source={require('@/assets/img/icons/attachment.png')}
                            />
                          </TouchableOpacity>
                          {showEditButton && (
                            <TouchableOpacity
                              style={styles.deleteButton}
                              onPress={() =>
                                deleteFile(detailData._id, 'foto_buku_rekening')
                              }>
                              <Image
                                style={{
                                  width: 20,
                                  height: 20,
                                  marginLeft: isWeb ? 10 : 0,
                                  marginRight: 100,
                                }}
                                width={20}
                                height={20}
                                svg={true}
                                source={require('@assets/img/icons/close-circle-blue.svg')}
                              />
                            </TouchableOpacity>
                          )}
                        </View>
                      ) : showEditButton ? (
                        <Layout.FormGroup>
                          <FileInput
                            confirmRemove
                            value={getFileNameFromUrl(
                              detailData.foto_buku_rekening,
                              'Foto Buku Rekening',
                            )}
                            onChange={(value) => {
                              saveFile(detailData._id, {
                                type: 'foto_buku_rekening',
                                url: value,
                              });
                            }}
                            containerStyleExtra={[
                              !isWeb
                                ? styles.mobileUploadForm
                                : styles.webUploadForm,
                              isWeb ? {} : styles.textStyleFix,
                            ]}
                          />
                        </Layout.FormGroup>
                      ) : (
                        '-'
                      )
                    }
                  />
                  <ItemDetail
                    testID="skp-external-detail-payment-proof"
                    nameComponent={
                      <SociollaText style={styles.label}>
                        Payment Proof
                      </SociollaText>
                    }
                    centered
                    value={
                      detailData.payment_proof ? (
                        <View
                          style={
                            isWeb
                              ? styles.webCardTextStyle
                              : styles.mobileCardTextStyle
                          }>
                          <FileInput
                            value={getFileNameFromUrl(
                              detailData.payment_proof,
                              'Payment Proof',
                            )}
                            showTrash={false}
                            containerStyleExtra={[
                              !isWeb
                                ? styles.mobileUploadForm
                                : styles.webUploadForm,
                              isWeb ? {} : styles.textStyleFix,
                            ]}
                          />
                          <TouchableOpacity
                            style={styles.uploadImageContainer}
                            onPress={() =>
                              downloadFile(
                                detailData.payment_proof,
                                'PaymentProof',
                                '',
                                true,
                              )
                            }>
                            <Image
                              style={{
                                width: 32,
                                height: 32,
                                marginRight: isWeb ? 100 : 0,
                              }}
                              width={32}
                              height={32}
                              isResolveAsset
                              source={require('@/assets/img/icons/attachment.png')}
                            />
                          </TouchableOpacity>
                          {showEditButton && (
                            <TouchableOpacity
                              style={styles.deleteButton}
                              onPress={() =>
                                deleteFile(detailData._id, 'payment_proof')
                              }>
                              <Image
                                style={{
                                  width: 20,
                                  height: 20,
                                  marginLeft: isWeb ? 10 : 0,
                                  marginRight: 100,
                                }}
                                width={20}
                                height={20}
                                svg={true}
                                source={require('@assets/img/icons/close-circle-blue.svg')}
                              />
                            </TouchableOpacity>
                          )}
                        </View>
                      ) : showEditButton ? (
                        <Layout.FormGroup>
                          <FileInput
                            confirmRemove
                            value={getFileNameFromUrl(
                              detailData.payment_proof,
                              'Payment Proof',
                            )}
                            onChange={(value) => {
                              saveFile(detailData._id, {
                                type: 'payment_proof',
                                url: value,
                              });
                            }}
                            containerStyleExtra={[
                              !isWeb
                                ? styles.mobileUploadForm
                                : styles.webUploadForm,
                              isWeb ? {} : styles.textStyleFix,
                            ]}
                          />
                        </Layout.FormGroup>
                      ) : (
                        '-'
                      )
                    }
                  />
                  <ItemDetail
                    testID="skp-external-detail-other-file"
                    nameComponent={
                      <SociollaText style={styles.label}>
                        Other File
                      </SociollaText>
                    }
                    centered
                    value={
                      detailData.other?.length || showEditButton ? (
                        <AttachmentsInput
                          testID="skp-external-detail-other-file"
                          showLabel={false}
                          readOnly={!showEditButton}
                          value={detailData.other || []}
                          onChange={(updatedAttachments) =>
                            setDetailData({
                              ...detailData,
                              other: updatedAttachments,
                            })
                          }
                          onUploadFile={(url) =>
                            saveFile(detailData._id, { type: 'other', url })
                          }
                          onRemoveFile={(item) =>
                            deleteOtherFile(detailData._id, item.url)
                          }
                        />
                      ) : (
                        '-'
                      )
                    }
                  />
                  <ItemDetail
                    testID="skp-external-detail-notes"
                    name="Notes"
                    value={detailData?.notes ? detailData.notes : '-'}
                  />
                </View>
              </View>
              {modalReject && (
                <ModalReject
                  testID="skp-external-detail-modal-reject"
                  show={modalReject}
                  onClose={() => setModalReject(false)}
                  onSubmit={changeStateToRejected}
                  title={'Reject SKP External'}
                />
              )}
              {modalCancel && (
                <ModalCancel
                  testID="skp-external-detail-modal-cancel"
                  show={modalCancel}
                  onClose={() => setModalCancel(false)}
                  onSubmit={(reason) => {
                    changeStatus(detailData?._id, 'cancelled', reason);
                  }}
                  title="Cancel SKP External"
                  description="This SKP External will be cancelled."
                />
              )}
              {modalDelete && (
                <ModalCancel
                  testID="skp-external-detail-modal-delete"
                  show={modalDelete}
                  onClose={() => setModalDelete(false)}
                  onSubmit={(reason) => {
                    removeSkp(detailData?._id, reason);
                    setModalDelete(false);
                  }}
                  title="Delete SKP External"
                  description="This SKP External will be deleted."
                />
              )}
              {showModalLog && (
                <ModalLog
                  testID="skp-external-detail-modal-log"
                  show={showModalLog}
                  status={detailData.status}
                  onClose={() => setShowModalLog(false)}
                  logType="skp_external"
                  logId={props.route.params.id}
                  logLabel="SKP External"
                  data={detailData}
                  logFormatter={SKP_EXTERNAL_LOG_FORMATTER}
                />
              )}
            </Layout.Wrapper>
            {!isWeb &&
            showEditButton &&
            ['draft', 'joined'].includes(detailData?.status) ? (
              <Layout.ButtonSticky
                testID="skp-external-detail-sticky-buttons"
                route={props?.route}
                show={props?.route?.name == 'SkpExternalDetailScreen'}
                buttons={[
                  detailData?.status == 'draft'
                    ? {
                        title: 'Customer Joined',
                        onPress: () => {
                          checkCalculation('joined');
                        },
                      }
                    : {},
                  {
                    title: 'Request Confirmation',
                    disabled: detailData?.status == 'joined' ? false : true,
                    onPress: () => {
                      checkCalculation('need_approval');
                    },
                  },
                ]}
              />
            ) : (
              false
            )}
            {!isWeb && canApprove() ? (
              <Layout.ButtonSticky
                route={props?.route}
                show={props?.route?.name == 'SkpExternalDetailScreen'}
                buttons={[
                  {
                    title: 'Reject',
                    type: 'primary',
                    onPress: () => {
                      setModalReject(true);
                    },
                  },
                  {
                    title: 'Confirm',
                    onPress: () => {
                      changeStatus(detailData?._id, 'approved');
                    },
                  },
                ]}
              />
            ) : (
              false
            )}
            {!isWeb &&
            showCompletedButton &&
            detailData?.status == 'approved' ? (
              <Layout.ButtonSticky
                route={props?.route}
                show={props?.route?.name == 'SkpExternalDetailScreen'}
                buttons={[
                  {
                    title: 'Completed',
                    onPress: () => {
                      changeStatus(detailData?._id, 'completed');
                    },
                  },
                ]}
              />
            ) : (
              false
            )}
          </MobileScroll>
        </>
      ) : (
        false
      )}
      {detailData._id ? (
        <>
          {isWeb ? (
            <FloatCommentButton
              onPress={() => setShowComments(true)}
              drawerVisible={showComments}
              entityType="skp_external"
              entityId={detailData._id}
            />
          ) : (
            false
          )}
          <CommentDrawer
            visible={showComments}
            onClose={() => setShowComments(false)}
            entityType="skp_external"
            entityId={detailData._id}
            entityRef={detailData.reference_code}
          />
        </>
      ) : (
        false
      )}
    </Layout.Container>
  );
};

const styles = StyleSheet.create({
  sectionWrapper: { width: '100%', padding: 15 },
  rowContainer: { flexDirection: 'row', alignItems: 'center' },
  mobileUploadForm: {
    display: 'flex',
    borderRadius: 4,
    borderWidth: 1,
    height: 45,
    borderColor: Colors.grey0,
    width: isWebPlatform ? '100%' : isAndroid ? '70%' : '50%',
    padding: 3,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    flexWrap: 'wrap',
    overflow: 'hidden',
    marginLeft: isWebPlatform ? 0 : isAndroid ? 50 : 100,
  },
  webUploadForm: {
    borderRadius: 4,
    borderWidth: 1,
    height: 45,
    paddingHorizontal: 16,
    borderColor: Colors.grey0,
    width: '100%',
    marginRight: 0,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  textStyleFix: {
    textAlign: 'center',
    maxWidth: '100%',
    lineHeight: 17,
  },
  webCardTextStyle: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    width: 145,
  },
  mobileCardTextStyle: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    width: isWebPlatform ? 130 : 180,
    marginLeft: isWebPlatform ? 20 : 200,
  },
  uploadImageContainer: { width: 36, marginLeft: !isWeb ? 20 : 18 },
  deleteButton: {
    width: 20,
    marginLeft: !isWeb ? 10 : 0,
    marginRight: isWebPlatform ? 0 : isAndroid ? 100 : 200,
    paddingVertical: 10,
  },
  label: {
    fontSize: 12,
    color: Colors.grey1,
  },
  required: {
    fontSize: 16,
    color: Colors.blue0,
    paddingLeft: 2,
    marginTop: -2,
  },
  row: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
  },
});

export default SkpExternalDetailScreen;
